import React, { useState, useEffect, useRef, useCallback } from 'react';
import apiClient from '../../services/apiClient';
import { toast } from 'sonner';
import {
    X, ChevronRight, ChevronLeft, Check, BookOpen, GraduationCap,
    Monitor, MapPin, User, Phone, Mail, Shield, Sparkles, Clock,
    Loader2, ArrowRight, CheckCircle2, Navigation, Home, Target,
    Globe, Users, School, UserRound, Calendar, Star
} from 'lucide-react';
import { useLoadScript } from '@react-google-maps/api';

// Step components
import CategoryStep from './steps/CategoryStep';
import ClassDetailsStep from './steps/ClassDetailsStep';
import PreferencesStep from './steps/PreferencesStep';
import ContactStep from './steps/ContactStep';
import VerifyOtpStep from './steps/VerifyOtpStep';

// Step configuration
const STEPS = [
    { id: 1, title: 'What do you need?', icon: BookOpen },
    { id: 2, title: 'Class Details', icon: GraduationCap },
    { id: 3, title: 'Preferences', icon: MapPin },
    { id: 4, title: 'Your Details', icon: User },
    { id: 5, title: 'Verify Phone', icon: Shield },
];

// Category options
const CATEGORIES = [
    { value: 'home_tuition', label: 'Home Tuition', icon: Home, description: 'One-on-one at your home' },
    { value: 'online_tuition', label: 'Online Tuition', icon: Monitor, description: 'Learn from anywhere' },
    { value: 'group_classes', label: 'Group Classes', icon: Users, description: 'Learn with peers' },
    { value: 'coaching', label: 'Coaching', icon: Target, description: 'Exam preparation' },
];

const BOARDS = [
    { value: 'cbse', label: 'CBSE' },
    { value: 'icse', label: 'ICSE' },
    { value: 'state_board', label: 'State Board' },
    { value: 'international_baccalaureate', label: 'IB' },
    { value: 'igcse', label: 'IGCSE' },
    { value: 'none', label: 'Other / None' },
];

const CLASS_MODES = [
    { value: 'online', label: 'Online', icon: Globe, desc: 'Virtual classes' },
    { value: 'offline_student_place', label: "At Student's Place", icon: Home, desc: 'Tutor comes to you' },
    { value: 'offline_tutor_place', label: "At Tutor's Place", icon: MapPin, desc: 'Go to tutor' },
    { value: 'nearby_classes', label: 'Nearby Classes', icon: School, desc: 'Local coaching' },
];

const GENDERS = [
    { value: 'no_preference', label: 'No Preference', icon: Sparkles },
    { value: 'male', label: 'Male Tutor', icon: UserRound },
    { value: 'female', label: 'Female Tutor', icon: UserRound },
];

const AVAILABILITY = [
    { value: 'weekdays', label: 'Weekdays', icon: Calendar },
    { value: 'weekends', label: 'Weekends', icon: Star },
    { value: 'any', label: 'Anytime', icon: Clock },
];

const POPULAR_SUBJECTS = [
    'Mathematics', 'Physics', 'Chemistry', 'Biology', 'English',
    'Hindi', 'Computer Science', 'Economics', 'Accounts', 'History',
    'Geography', 'Science', 'Social Studies'
];

const GRADES = [
    { value: 'lkg', label: 'LKG / UKG' },
    { value: '1-5', label: 'Class 1 to 5' },
    { value: '6-8', label: 'Class 6 to 8' },
    { value: '9-10', label: 'Class 9 & 10' },
    { value: '11-12', label: 'Class 11 & 12' },
    { value: 'bachelors', label: 'Bachelors / Degree' },
    { value: 'masters', label: 'Masters / Post Grad' },
    { value: 'competitive', label: 'Competitive Exams' },
    { value: 'music_arts', label: 'Music / Arts / Hobby' },
    { value: 'language', label: 'Spoken English / Languages' },
];

const LIBRARIES = ['places'];

export const BookDemoModal = ({ isOpen, onClose }) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [direction, setDirection] = useState('forward');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isComplete, setIsComplete] = useState(false);

    // Form data
    const [formData, setFormData] = useState({
        category: '',
        subjects: [],
        subjectInput: '',
        board: 'cbse',
        grade: '',
        class_mode: 'offline_student_place',
        budget_type: 'monthly',
        budget_amount: 5000,
        gender_preference: 'no_preference',
        availability: ['any'],
        city: '',
        state: '',
        latitude: null,
        longitude: null,
        full_name: '',
        email: '',
        phone: '',
    });

    // Map State
    const [showMap, setShowMap] = useState(false);
    const mapRef = useRef(null);
    const autocompleteRef = useRef(null);

    const { isLoaded } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
        libraries: LIBRARIES,
    });

    // OTP state
    const [otpSending, setOtpSending] = useState(false);
    const [otpSent, setOtpSent] = useState(false);
    const [otpValues, setOtpValues] = useState(['', '', '', '', '', '']);
    const [otpVerifying, setOtpVerifying] = useState(false);
    const [otpToken, setOtpToken] = useState(null);
    const [phoneVerified, setPhoneVerified] = useState(false);
    const [timer, setTimer] = useState(0);
    const [resendCooldown, setResendCooldown] = useState(0);
    const otpInputRefs = useRef([]);

    // Timer countdown
    useEffect(() => {
        let interval;
        if (timer > 0) {
            interval = setInterval(() => setTimer(t => t - 1), 1000);
        }
        return () => clearInterval(interval);
    }, [timer]);

    useEffect(() => {
        let interval;
        if (resendCooldown > 0) {
            interval = setInterval(() => setResendCooldown(t => t - 1), 1000);
        }
        return () => clearInterval(interval);
    }, [resendCooldown]);

    // Reset on close
    useEffect(() => {
        if (!isOpen) {
            setTimeout(() => {
                setCurrentStep(1);
                setDirection('forward');
                setIsComplete(false);
                setOtpSent(false);
                setOtpToken(null);
                setPhoneVerified(false);
                setOtpValues(['', '', '', '', '', '']);
                setFormData({
                    category: '', subjects: [], subjectInput: '', board: 'cbse', grade: '',
                    class_mode: 'offline_student_place', budget_type: 'monthly',
                    budget_amount: 5000, gender_preference: 'no_preference',
                    availability: ['any'], city: '', state: '', latitude: null, longitude: null,
                    full_name: '', email: '', phone: ''
                });
                setShowMap(false);
            }, 300);
        }
    }, [isOpen]);

    // Prevent body scroll
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [isOpen]);

    const updateField = useCallback((field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    }, []);

    const addSubject = (subject) => {
        if (subject && !formData.subjects.includes(subject)) {
            setFormData(prev => ({ ...prev, subjects: [...prev.subjects, subject], subjectInput: '' }));
        }
    };

    const removeSubject = (subject) => {
        setFormData(prev => ({ ...prev, subjects: prev.subjects.filter(s => s !== subject) }));
    };

    const toggleAvailability = (value) => {
        setFormData(prev => {
            const current = prev.availability;
            if (value === 'any') return { ...prev, availability: ['any'] };
            const filtered = current.filter(v => v !== 'any');
            if (filtered.includes(value)) {
                const result = filtered.filter(v => v !== value);
                return { ...prev, availability: result.length ? result : ['any'] };
            }
            return { ...prev, availability: [...filtered, value] };
        });
    };

    const handlePlaceSelect = useCallback(() => {
        const place = autocompleteRef.current?.getPlace();
        if (!place || !place.geometry) return;
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        let city = '';
        let state = '';
        for (const component of (place.address_components || [])) {
            if (component.types.includes('locality')) city = component.long_name;
            if (component.types.includes('administrative_area_level_1')) state = component.long_name;
        }
        setFormData(prev => ({
            ...prev,
            city: city || place.name || prev.city,
            state: state || prev.state,
            latitude: lat,
            longitude: lng,
        }));
        setShowMap(true);
    }, []);

    const handleUseCurrentLocation = useCallback(() => {
        if (!navigator.geolocation) {
            toast.error('Geolocation is not supported by your browser');
            return;
        }
        navigator.geolocation.getCurrentPosition(
            (position) => {
                const lat = position.coords.latitude;
                const lng = position.coords.longitude;
                setFormData(prev => ({ ...prev, latitude: lat, longitude: lng }));
                setShowMap(true);
                toast.success('Location detected!');
            },
            () => toast.error('Unable to get your location. Please allow location access.')
        );
    }, []);

    // Validation per step
    const canProceed = () => {
        switch (currentStep) {
            case 1: return formData.category && formData.subjects.length > 0;
            case 2: return formData.board && formData.class_mode && formData.grade;
            case 3: return formData.city.trim().length > 0;
            case 4: return formData.full_name.trim() && formData.email.trim() && formData.phone.trim().length >= 10;
            case 5: return phoneVerified;
            default: return true;
        }
    };

    const goNext = () => {
        if (currentStep < 5 && canProceed()) {
            setDirection('forward');
            setCurrentStep(prev => prev + 1);
        }
    };

    const goBack = () => {
        if (currentStep > 1) {
            setDirection('backward');
            setCurrentStep(prev => prev - 1);
        }
    };

    // OTP Functions
    const sendOtp = async () => {
        if (!formData.phone || formData.phone.trim().length < 10) {
            toast.error('Please enter a valid 10-digit phone number');
            return;
        }
        setOtpSending(true);
        try {
            await apiClient.post('/auth/send-otp-demo', { phone: formData.phone.trim() });
            setOtpSent(true);
            setTimer(300); // 5 min expiry
            setResendCooldown(30);
            toast.success('OTP sent to your phone!');
        } catch (error) {
            const msg = error.response?.data?.detail || 'Failed to send OTP';
            toast.error(typeof msg === 'string' ? msg : 'Failed to send OTP');
        } finally {
            setOtpSending(false);
        }
    };

    const handleOtpChange = (index, value) => {
        if (value.length > 1) value = value.slice(-1);
        if (!/^\d*$/.test(value)) return;

        const newValues = [...otpValues];
        newValues[index] = value;
        setOtpValues(newValues);

        if (value && index < 5) {
            otpInputRefs.current[index + 1]?.focus();
        }
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otpValues[index] && index > 0) {
            otpInputRefs.current[index - 1]?.focus();
        }
    };

    const handleOtpPaste = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
        const newValues = [...otpValues];
        for (let i = 0; i < 6; i++) {
            newValues[i] = pasted[i] || '';
        }
        setOtpValues(newValues);
        const nextEmpty = newValues.findIndex(v => !v);
        if (nextEmpty >= 0) otpInputRefs.current[nextEmpty]?.focus();
        else otpInputRefs.current[5]?.focus();
    };

    const verifyOtp = async () => {
        const otp = otpValues.join('');
        if (otp.length !== 6) {
            toast.error('Please enter the complete 6-digit OTP');
            return;
        }
        setOtpVerifying(true);
        try {
            const res = await apiClient.post('/auth/verify-otp-demo', {
                phone: formData.phone.trim(),
                otp
            });
            setOtpToken(res.otp_token);
            setPhoneVerified(true);
            toast.success('Phone verified successfully!');
        } catch (error) {
            const msg = error.response?.data?.detail || 'Invalid OTP';
            toast.error(typeof msg === 'string' ? msg : 'Invalid OTP');
        } finally {
            setOtpVerifying(false);
        }
    };

    // Submit the demo booking
    const submitDemo = async () => {
        if (!otpToken) {
            toast.error('Please verify your phone first');
            return;
        }
        setIsSubmitting(true);
        try {
            const payload = {
                full_name: formData.full_name.trim(),
                email: formData.email.trim(),
                phone: formData.phone.trim(),
                otp_token: otpToken,
                category: formData.category,
                subjects: formData.subjects,
                board: formData.board,
                class_mode: formData.class_mode,
                grade: formData.grade,
                availability: formData.availability,
                gender_preference: formData.gender_preference,
                budget_type: formData.budget_type,
                budget_amount: formData.budget_amount,
                city: formData.city.trim(),
                state: formData.state.trim(),
                latitude: formData.latitude,
                longitude: formData.longitude,
                show_map: !!formData.latitude,
                description: `Looking for ${formData.category.replace('_', ' ')} for ${formData.subjects.join(', ')}`,
            };
            const res = await apiClient.post('/book-demo', payload);
            setIsComplete(true);
            toast.success(res.message);
        } catch (error) {
            const msg = error.response?.data?.detail || 'Failed to book demo';
            toast.error(typeof msg === 'string' ? msg : 'Failed to book demo');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    // Success screen
    if (isComplete) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
                <div className="absolute inset-0 bg-black/60 backdrop-blur-md" />
                <div className="relative bg-white rounded-3xl p-8 max-w-md w-full text-center shadow-2xl animate-in zoom-in-95 duration-300" onClick={e => e.stopPropagation()}>
                    <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center shadow-lg shadow-green-200">
                        <CheckCircle2 className="h-10 w-10 text-white" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Demo Booked! 🎉</h2>
                    <p className="text-gray-600 mb-6">
                        We've received your requirement. Our team will connect you with the best tutors in your area within 24 hours.
                    </p>
                    <p className="text-sm text-gray-500 mb-6 bg-blue-50 p-3 rounded-xl">
                        Your login credentials have been sent to your phone. You can track your request on the dashboard.
                    </p>
                    <button
                        onClick={onClose}
                        className="w-full py-3 px-6 bg-gradient-to-r from-[#041f96] to-[#0633d4] text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-blue-200 transition-all duration-300"
                    >
                        Done
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/60 backdrop-blur-md" onClick={onClose} />

            {/* Modal */}
            <div className="relative bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300" onClick={e => e.stopPropagation()}>

                {/* Header with progress */}
                <div className="relative bg-gradient-to-r from-[#041f96] to-[#0633d4] px-6 py-5 text-white">
                    <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/20 transition-colors">
                        <X className="h-5 w-5" />
                    </button>
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-white/20 rounded-xl">
                            <Sparkles className="h-5 w-5" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold">Book Free Demo</h2>
                            <p className="text-sm text-blue-200">Step {currentStep} of 5 — {STEPS[currentStep - 1].title}</p>
                        </div>
                    </div>

                    {/* Progress bar */}
                    <div className="flex gap-1.5">
                        {STEPS.map((step) => (
                            <div key={step.id} className="flex-1 h-1.5 rounded-full overflow-hidden bg-white/20">
                                <div
                                    className="h-full rounded-full transition-all duration-500 ease-out"
                                    style={{
                                        width: step.id < currentStep ? '100%' : step.id === currentStep ? '50%' : '0%',
                                        background: step.id <= currentStep ? 'rgba(255,255,255,0.9)' : 'transparent'
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                </div>

                {/* Content — delegated to step components */}
                <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 180px)' }}>
                    {currentStep === 1 && (
                        <CategoryStep
                            formData={formData}
                            updateField={updateField}
                            addSubject={addSubject}
                            removeSubject={removeSubject}
                            categories={CATEGORIES}
                            popularSubjects={POPULAR_SUBJECTS}
                        />
                    )}

                    {currentStep === 2 && (
                        <ClassDetailsStep
                            formData={formData}
                            updateField={updateField}
                            boards={BOARDS}
                            grades={GRADES}
                            classModes={CLASS_MODES}
                        />
                    )}

                    {currentStep === 3 && (
                        <PreferencesStep
                            formData={formData}
                            updateField={updateField}
                            toggleAvailability={toggleAvailability}
                            genders={GENDERS}
                            availability={AVAILABILITY}
                            isLoaded={isLoaded}
                            autocompleteRef={autocompleteRef}
                            handlePlaceSelect={handlePlaceSelect}
                            handleUseCurrentLocation={handleUseCurrentLocation}
                            showMap={showMap}
                            setShowMap={setShowMap}
                        />
                    )}

                    {currentStep === 4 && (
                        <ContactStep
                            formData={formData}
                            updateField={updateField}
                        />
                    )}

                    {currentStep === 5 && (
                        <VerifyOtpStep
                            formData={formData}
                            phoneVerified={phoneVerified}
                            otpSent={otpSent}
                            otpSending={otpSending}
                            otpValues={otpValues}
                            otpVerifying={otpVerifying}
                            timer={timer}
                            resendCooldown={resendCooldown}
                            otpInputRefs={otpInputRefs}
                            sendOtp={sendOtp}
                            handleOtpChange={handleOtpChange}
                            handleOtpKeyDown={handleOtpKeyDown}
                            handleOtpPaste={handleOtpPaste}
                            verifyOtp={verifyOtp}
                            submitDemo={submitDemo}
                            isSubmitting={isSubmitting}
                        />
                    )}
                </div>

                {/* Footer navigation (steps 1-4 only) */}
                {currentStep < 5 && (
                    <div className="border-t border-gray-100 px-6 py-4 flex items-center justify-between bg-gray-50/50">
                        <button
                            onClick={goBack}
                            disabled={currentStep === 1}
                            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >
                            <ChevronLeft className="h-4 w-4" /> Back
                        </button>

                        <div className="flex items-center gap-1.5">
                            {STEPS.map(step => (
                                <div
                                    key={step.id}
                                    className={`w-2 h-2 rounded-full transition-all duration-300 ${step.id === currentStep ? 'w-6 bg-[#041f96]' :
                                        step.id < currentStep ? 'bg-[#041f96]' : 'bg-gray-300'
                                        }`}
                                />
                            ))}
                        </div>

                        <button
                            onClick={goNext}
                            disabled={!canProceed()}
                            className="flex items-center gap-2 px-6 py-2.5 bg-[#041f96] text-white text-sm font-semibold rounded-xl hover:bg-[#031580] disabled:opacity-40 disabled:cursor-not-allowed transition-all hover:shadow-md"
                        >
                            Next <ChevronRight className="h-4 w-4" />
                        </button>
                    </div>
                )}

                {/* Back button for step 5 */}
                {currentStep === 5 && !phoneVerified && (
                    <div className="border-t border-gray-100 px-6 py-4 bg-gray-50/50">
                        <button
                            onClick={goBack}
                            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                        >
                            <ChevronLeft className="h-4 w-4" /> Back to Details
                        </button>
                    </div>
                )}
            </div >
        </div >
    );
};
